import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

/**
 * URL-backed filter state. `set` starts from the LIVE URL (history is updated synchronously) rather
 * than from the rendered params: React Router applies navigations in a transition, so a second change
 * issued before re-render (e.g. "Clear filters" then a new filter) must not resurrect stale values.
 */
export function useUrlState() {
  const [sp, setSp] = useSearchParams();
  const set = (patch: Record<string, string | null>, resetPage = true) => {
    const next = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k);
    if (resetPage) next.delete('offset');
    setSp(next, { replace: true });
  };
  const reset = () => setSp(new URLSearchParams(), { replace: true });
  return { sp, set, reset };
}

/** A search box bound to a URL param, written to the URL 300 ms after the user stops typing. */
export function useSearchText(key = 'q') {
  const { sp, set } = useUrlState();
  const [text, setText] = useState(sp.get(key) ?? '');
  const setRef = useRef(set);
  setRef.current = set;
  useEffect(() => {
    const h = setTimeout(() => {
      const current = new URLSearchParams(window.location.search).get(key) ?? '';
      if (current !== text) setRef.current({ [key]: text || null });
    }, 300);
    return () => clearTimeout(h);
  }, [text, key]);
  return [text, setText] as const;
}
